"use client";

import {
  AppHeroSection,
  AppFeaturesSection,
  AppPricingSection,
  AppFAQSection,
  AppGuidesSection,
  AppBannerShowcase,
  AppTeamSection,
} from "@/components/custom/shared";
import { loyaltyFaqs, loyaltyGuides } from "./data";

// Loyalty-specific data
const loyaltyFeatures = [
  {
    title: "Reward customers",
    description:
      "with points for purchases, sign-ups, or birthdays, redeemable for discounts, free shipping, or exclusive products.",
    image: "/images/LR-Feature-1.webp",
  },
  {
    title: "VIP Tiers",
    description:
      "create multiple loyalty levels with escalating benefits to recognize your best customers, motivate them to spend more, and drive long-term retention.",
    image: "/images/LR-Feature-2.webp",
  },
  {
    title: "Referrals.",
    description:
      "Grow your customer base by rewarding existing shoppers for referring friends and family, offering incentives to both the referrer and the new customer.",
    image: "/images/LR-Feature-3.webp",
  },
];

const loyaltyPricingPlans = [
  {
    name: "Free",
    price: 0,
    ctaText: "Start 14-day free trial",
    ctaLink:
      "https://apps.shopify.com/keystone-loyalty-rewards?utm_source=website&utm_medium=organic&utm_campaign=freeplan",
    features: [
      "Up to 250 monthly orders",
      "Loyalty points - Basic",
      "Discount rewards - Basic",
      "Dynamic discount codes - Basic",
      "Data import/export",
      "Storefront widget",
    ],
  },
  {
    name: "Starter",
    price: 14.99,
    ctaText: "Start 14-day free trial",
    ctaLink:
      "https://apps.shopify.com/keystone-loyalty-rewards?utm_source=website&utm_medium=organic&utm_campaign=starterplan",
    includesLabel: "Everything in Free, plus:",
    features: [
      "Up to 500 monthly orders",
      "Gift card rewards",
      "Points expiry & notifications",
      "Custom rewards & emails",
      "Referrals & campaigns",
      "POS rewards for in-store customers",
      "Embed loyalty program on product pages",
    ],
  },
  {
    name: "Standard",
    price: 69,
    popular: true,
    ctaText: "Start 14-day free trial",
    ctaLink:
      "https://apps.shopify.com/keystone-loyalty-rewards?utm_source=website&utm_medium=organic&utm_campaign=standardplan",
    includesLabel: "Everything in Starter, plus:",
    features: [
      "Up to 1,250 monthly orders",
      "Loyalty points - Advanced",
      "Discount rewards - Advanced",
      "Dynamic discount codes - Advanced",
      "VIP tiers program",
    ],
    highlight: {
      icon: "magic",
      label: "We set it all up for you",
      sublabel: "Tell us your goals - our team builds your entire program.",
    },
  },
  {
    name: "Growth",
    price: 99,
    additionalPriceLabel: "$20 per additional 100 orders",
    ctaText: "Start 14-day free trial",
    ctaLink:
      "https://apps.shopify.com/keystone-loyalty-rewards?utm_source=website&utm_medium=organic&utm_campaign=growthplan",
    includesLabel: "Everything in Standard, plus:",
    features: [
      "2,000 monthly orders included",
      "All advanced features",
      "Show point balance on customer accounts",
      "Multi-language storefront",
      "Supports multi-currency",
      "Trigger rewards with Shopify Flow",
      "Integrations (e.g. Klaviyo, Mailchimp, etc.)",
    ],
    highlight: {
      icon: "magic",
      label: "We set it all up for you",
      sublabel: "Tell us your goals - our team builds your entire program.",
    },
  },
  {
    name: "Plus",
    price: 399,
    additionalPriceLabel: "$10 per additional 100 orders",
    ctaText: "Start 14-day free trial",
    ctaLink:
      "https://apps.shopify.com/keystone-loyalty-rewards?utm_source=website&utm_medium=organic&utm_campaign=plusplan",
    includesLabel: "Everything in Growth, plus:",
    features: [
      "5,000 monthly orders included",
      "All advanced features",
      "Redeem at checkout (Shopify Plus)",
    ],
    highlight: {
      icon: "person",
      label: "We run everything for you, all year",
      sublabel:
        "A dedicated manager sets up, runs & optimizes your program - you never lift a finger.",
    },
  },
];

const loyaltyTeam = [
  // Headshots are pre-cropped to a common framing (face ~30% of frame height,
  // centred at 51%/41%), so every avatar needs the default centre position.
  {
    name: "Haris",
    role: "Full Stack Developer",
    image: "/images/team/haris.jpg",
  },
  {
    name: "Daniel",
    role: "Software Architect & Tech Lead",
    image: "/images/team/daniel.jpg",
  },
  {
    name: "Amina",
    role: "Quality Assurance",
    image: "/images/team/amina.jpg",
  },
  {
    name: "Belma",
    role: "Marketing Manager",
    image: "/images/team/belma.png",
  },
  {
    name: "Zekerijah",
    role: "Backend Developer",
    image: "/images/team/zekerijah.jpg",
  },
  {
    name: "Vedran Kolovrat",
    role: "Product Manager",
    image: "/images/team/vedran.png",
  },
];


const LoyaltyPage = () => {
  return (
    <>
      {/* Hero Section */}
      <AppHeroSection
        brand="Keystone"
        title="Loyalty & Rewards"
        description="Turn engagement into growth with a loyalty program that fits your brand's customer journey."
        ctaText="Install now"
        ctaLink="https://apps.shopify.com/keystone-loyalty-rewards?utm_source=website&utm_medium=organic&utm_campaign=productpageheader"
        secondaryCtaText="View demo store"
        secondaryCtaLink="https://keystone-fashion-loyalty-rewards-demo.myshopify.com/"
        iconSrc="/brands/loyalty-square.svg"
        heroImage="/images/Loyalty-Hero-Card.png"
        heroImageTablet="/images/Loyalty-Hero-Card.png"
        heroImageMobile="/images/Loyalty-Hero-Card.png"
        backgroundSvg="/loyalty-background.svg"
      />

      {/* Features Section */}
      <AppFeaturesSection
        title="Designed to convert. Built to scale."
        features={loyaltyFeatures}
      />

      {/* Pricing Section — commented out */}
      <AppPricingSection
        id="pricing"
        title="Pricing"
        plans={loyaltyPricingPlans}
        accentColor="#8c67ec"
        popularBorderColor="#5a188b"
        popularBorderGradient="linear-gradient(90deg, #5A188B 0%, #69FFE6 50%, #9CE4D3 100%)"
        popularBadgeColor="#7c51de"
        popularButtonGlowColor="rgba(61,237,208,0.4)"
        showBillingToggle={false}
        popularButtonGradient="linear-gradient(34.36deg, rgb(157, 68, 248) 0.22%, rgb(104, 178, 210) 99.18%)"
        uniformButtons
      />

      {/* Meet the Team */}
      <AppTeamSection
        id="team"
        title="Meet the team"
        description="The people behind Keystone Loyalty & Rewards — building, testing and running your loyalty program."
        members={loyaltyTeam}
      />

      {/* Banner Showcase */}
      <AppBannerShowcase
        title={
          <>
            Make customer loyalty the{"\n"}keystone of your store&apos;s growth.
          </>
        }
        description="Build a brand that people love to come back to. Launch custom rewards and VIP perks to grow your community and your sales naturally."
        ctaText="Install now"
        ctaLink="https://apps.shopify.com/keystone-loyalty-rewards?utm_source=website&utm_medium=organic&utm_campaign=productpagefooter"
        secondaryCtaText="View demo store"
        secondaryCtaLink="https://keystone-fashion-loyalty-rewards-demo.myshopify.com/"
        backgroundImage="/images/loyalty-prefooter-bg.webp"
      />

      {/* FAQ Section */}
      <AppFAQSection title="FAQs" faqs={loyaltyFaqs} />

      <AppGuidesSection title="Loyalty program guides" guides={loyaltyGuides} />
    </>
  );
};

export default LoyaltyPage;
